import type { Apis, SessionService } from '@efdigital/ll-addon-auth';
import {
  canTransitionToStatus,
  type CoachingPartnershipTransitionDecision,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import {
  isCancelled,
  isCompleted,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import {
  type CoachingPartnership,
  type CoachingPartnershipStatus,
  type CoachingPlan,
  type CoachingSession,
  type CoachingSessionType,
} from '@efdigital/ll-addon-data';
import type {
  CoacheeGetQuotaResponseBody,
  CoacheePostAttendanceMarkRequestBody,
  CoacheePostFeedbackRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import {
  FeedbackAlreadySubmittedError,
  queryCoachingFeedbackByPartnership,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-feedback.ts';
import { queryCoachingPartnershipsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships.ts';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions.ts';
import {
  buildCoacheeGetQuotaRequest,
  parseCoacheeGetQuotaResponse,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/sessions.ts';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee.ts';
import { messageablePartnerships } from '@efdigital/ll-xceed-v2/utils/coaching/partnership-messages';
import { cached, tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

export type CoachingPlannerProps = {
  store: Store;
  coachee: CoacheeService;
  session: SessionService;
  apis: Apis;
};

/**
 * Counts the attended sessions a coachee had with a coach.
 *
 * Mirrors ll-api `countAttendedSessionsForPartnership`, which gates the
 * feedback write route: a session counts when its `status` is `attended` or
 * its `resolvedOutcome` is `attended`. ll-api matches on the partnership id;
 * the planner's sessions are already scoped to one coaching plan, where a
 * coach has at most one partnership, so matching on `coachUserId` is the same
 * set without resolving the async `coachingPartnership` relationship.
 */
export function countAttendedSessions(
  sessions: CoachingSession[],
  coachUserId: string
): number {
  return sessions.filter(
    (session) =>
      session.coachUserId === coachUserId &&
      (session.status === 'attended' || session.resolvedOutcome === 'attended')
  ).length;
}

export class CoachingPlanner {
  @tracked coachingPlan: CoachingPlan;
  @tracked allPartnerships: CoachingPartnership[];
  @tracked allSessions: CoachingSession[];
  @tracked quota: CoacheeGetQuotaResponseBody;
  /**
   * Whether feedback has already been submitted for the chosen partnership.
   * `undefined` until checked, so `partnershipAwaitingFeedback` doesn't briefly
   * flash the ask before the check completes.
   */
  @tracked feedbackAlreadyGiven?: boolean;

  private constructor(
    private props: CoachingPlannerProps,
    coachingPlan: CoachingPlan
  ) {
    this.coachingPlan = coachingPlan;
    this.allPartnerships = [];
    this.allSessions = [];
    this.quota = {
      coachingPlanId: coachingPlan.id!,
      chemistry: {
        allocated: 0,
        granted: 0,
        remaining: 0,
        unit: 'calls',
      },
      coaching: {
        allocated: 0,
        granted: 0,
        remaining: 0,
        unit: 'minutes',
      },
    };
  }

  ///// --- Data loading

  static async load(
    coachingPlan: CoachingPlan,
    props: CoachingPlannerProps
  ): Promise<CoachingPlanner> {
    const manager = new CoachingPlanner(props, coachingPlan);
    await manager.#reloadPartnerships();
    await manager.#reloadSessionsAndQuota();
    await manager.#reloadFeedbackStatusOrHide();
    return manager;
  }

  async #reloadPartnerships() {
    const coachingPartnershipQuery = queryCoachingPartnershipsByCoachingPlan(
      this.coachingPlan.id!
    );
    const findExisting = await this.props.store.request(
      coachingPartnershipQuery
    );
    this.allPartnerships = findExisting.content.data;
  }

  /**
   * Checks whether feedback already exists for the chosen partnership.
   *
   * Only meaningful once that partnership could take feedback at all (see
   * `#feedbackEligiblePartnership`) — otherwise there is nothing to check, so
   * this leaves `feedbackAlreadyGiven` as `true` (never awaiting) rather than
   * issuing a query with nothing to find.
   */
  async #reloadFeedbackStatus() {
    const partnership = this.#feedbackEligiblePartnership();
    if (!partnership) {
      this.feedbackAlreadyGiven = true;
      return;
    }

    const existing = await this.props.store.request(
      queryCoachingFeedbackByPartnership(partnership.id!)
    );
    this.feedbackAlreadyGiven = existing.content.data.length > 0;
  }

  /**
   * The feedback check is non-essential: a failure must not take the whole
   * coachee dashboard down with it, so it logs and hides the banner instead.
   */
  async #reloadFeedbackStatusOrHide() {
    try {
      await this.#reloadFeedbackStatus();
    } catch (error) {
      console.error('Failed to check coaching feedback status', error);
      this.feedbackAlreadyGiven = true;
    }
  }

  async #reloadSessionsAndQuota() {
    const querySessions = queryCoachingSessionsByCoachingPlan(
      this.coachingPlan.id!
    );
    const sessions = await this.props.store.request(querySessions);

    const queryQuota = await buildCoacheeGetQuotaRequest(
      this.props.apis,
      this.props.session,
      this.coachingPlan.id!
    );

    const response = await fetch(queryQuota);
    const quota = await parseCoacheeGetQuotaResponse(response);

    this.allSessions = sessions.content.data;
    this.quota = quota;
  }

  /// ---- Business logic
  canTransitionToStatus = (
    coachUserId: string,
    nextStatus: CoachingPartnershipStatus
  ): CoachingPartnershipTransitionDecision => {
    const currentStatus =
      this.allPartnerships.find(
        (partnership) => partnership.coachUserId === coachUserId
      )?.status ?? 'pending';

    // Admin unassign sets `reassigned`. Coachees cannot put this coach back
    // on a shortlist — that would undo a conflict-of-interest move.
    if ((currentStatus as string) === 'reassigned') {
      return {
        outcome: 'blocked',
        reason: 'An administrator has moved this coachee off this coach',
      };
    }

    const decision = canTransitionToStatus(
      currentStatus,
      nextStatus,
      this.coachHasBeenChosen
    );

    if (decision.outcome === 'blocked' || decision.outcome === 'no-op') {
      return decision;
    }

    if (currentStatus === 'chemistry' && nextStatus !== 'chosen') {
      if (this.hasUncancelledChemistrySession(coachUserId)) {
        return {
          outcome: 'blocked',
          reason:
            'Cannot remove from shortlist once a chemistry session has been booked',
        };
      }
    }

    if (nextStatus === 'chemistry') {
      if (
        !this.hasUncancelledChemistrySession(coachUserId) &&
        !this.hasChemistryQuota
      ) {
        // We will allow someone to re-shortlist a coach if they have previously had a chemistry session
        // as they might have changed their mind about the coach they want to work with
        return {
          outcome: 'blocked',
          reason:
            'Not enough chemistry quota remaining to shortlist more coaches',
        };
      }
    }

    if (nextStatus === 'chosen') {
      if (!this.hasCompletedChemistrySession(coachUserId)) {
        return {
          outcome: 'blocked',
          reason: 'Must book chemistry session first',
        };
      }
    }

    return decision;
  };

  getChemistrySessions = (coachUserId: string): CoachingSession[] => {
    return this.allSessions.filter(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry'
    );
  };

  getStatusForCoach = (coachUserId: string) => {
    return (
      this.allPartnerships.find(
        (partnership) => partnership.coachUserId === coachUserId
      )?.status ?? 'pending'
    );
  };

  isCoachReassigned = (coachUserId: string) => {
    return (this.getStatusForCoach(coachUserId) as string) === 'reassigned';
  };

  canBookChemistrySession = (coachUserId: string) => {
    return (
      !this.coachHasBeenChosen &&
      this.getStatusForCoach(coachUserId) === 'chemistry' &&
      this.hasChemistryQuota &&
      !this.hasUncancelledChemistrySession(coachUserId)
    );
  };

  canBookCoachingSession = (coachUserId: string) => {
    return (
      this.coachHasBeenChosen &&
      this.getStatusForCoach(coachUserId) === 'chosen' &&
      // Conclusion leaves the partnership `chosen`; the sessions API refuses
      // bookings once it has concluded, so don't offer the booking flow.
      !this.chosenCoachPartnership()?.concludedAt &&
      this.hasCoachingQuota
    );
  };

  get hasChemistryQuota() {
    return this.quota.chemistry.remaining > 0;
  }

  get hasCoachingQuota() {
    return this.quota.coaching.remaining > 0;
  }

  @cached
  get messagingPartnerships() {
    return messageablePartnerships(
      this.allPartnerships,
      'coachee',
      this.coachHasBeenChosen
    );
  }

  @cached
  get chemistryAndChosenPartnerships() {
    return [
      ...this.#partnershipsWithStatus('chosen'),
      ...this.#partnershipsWithStatus('chemistry'),
    ];
  }

  @cached
  get chosenPartnerships() {
    return this.#partnershipsWithStatus('chosen');
  }

  #partnershipsWithStatus(status: CoachingPartnershipStatus) {
    return this.allPartnerships.filter(
      (partnership) => partnership.status === status
    );
  }

  hasUncancelledChemistrySession = (coachUserId: string) => {
    return this.allSessions.some(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry' &&
        !isCancelled(session.status) &&
        session.status !== 'booking_abandoned'
    );
  };

  hasCompletedChemistrySession = (coachUserId: string) => {
    return this.allSessions.some(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry' &&
        isCompleted(session.status)
    );
  };

  chosenCoachPartnership = () => {
    return this.allPartnerships.find(
      (partnership) => partnership.status === 'chosen'
    );
  };

  get coachHasBeenChosen() {
    return this.allPartnerships.some(
      (partnership) => partnership.status === 'chosen'
    );
  }

  /**
   * The chosen partnership, when ll-api would accept feedback on it: it has
   * concluded and at least one of its sessions was attended. Without an
   * attended session the write route returns 422, so the ask never appears.
   */
  #feedbackEligiblePartnership(): CoachingPartnership | undefined {
    const partnership = this.chosenCoachPartnership();
    if (!partnership?.concludedAt) {
      return undefined;
    }

    return countAttendedSessions(this.allSessions, partnership.coachUserId) > 0
      ? partnership
      : undefined;
  }

  /**
   * The chosen partnership, when it is eligible for feedback and has none yet.
   *
   * `undefined` while `feedbackAlreadyGiven` is still being checked, so a
   * banner built on this getter doesn't flash on then off while the request
   * that could hide it is still in flight.
   */
  get partnershipAwaitingFeedback(): CoachingPartnership | undefined {
    if (this.feedbackAlreadyGiven !== false) {
      return undefined;
    }

    return this.#feedbackEligiblePartnership();
  }

  // ------ Actions

  bookSession = async (
    coachUserId: string,
    sessionType: CoachingSessionType,
    idempotencyKey: string,
    startTime: string,
    endTime: string,
    notes?: string
  ) => {
    const partnership = this.allPartnerships.find(
      (partnership) => partnership.coachUserId === coachUserId
    )!;

    await this.props.coachee.bookSession(
      partnership,
      sessionType,
      idempotencyKey,
      startTime,
      endTime,
      notes
    );

    await this.#reloadSessionsAndQuota();
  };

  cancelSession = async (
    coachingSession: CoachingSession,
    cancelReason: string
  ) => {
    await this.props.coachee.cancelSession(coachingSession, cancelReason);

    await this.#reloadSessionsAndQuota();
  };

  setStatusForCoach = async (
    coachUserId: string,
    status: CoachingPartnershipStatus
  ) => {
    const hadExistingPartnership = this.allPartnerships.some(
      (partnership) => partnership.coachUserId === coachUserId
    );

    await this.props.coachee.setStatusForCoach(
      this.coachingPlan,
      coachUserId,
      status
    );

    if (!hadExistingPartnership) {
      await this.#reloadPartnerships();
    }
  };

  markAttendance = async (
    coachingSession: CoachingSession,
    body: CoacheePostAttendanceMarkRequestBody
  ) => {
    await this.props.coachee.markAttendance(coachingSession, body);

    await this.#reloadSessionsAndQuota();
  };

  /**
   * Submits feedback for the concluded partnership.
   *
   * Anonymous to the coach on the read side (see the ll-api security filter);
   * on this side, the only guard is that there is an eligible partnership to
   * submit against at all. ll-api rejects a resubmission with 409 — feedback
   * is already recorded (another tab, the email link), so that counts as done
   * rather than as a failure.
   */
  giveFeedback = async (body: CoacheePostFeedbackRequestBody) => {
    const partnership = this.partnershipAwaitingFeedback;
    if (!partnership) {
      throw new Error('No concluded partnership is awaiting feedback');
    }

    try {
      await this.props.coachee.giveFeedback(partnership.id!, body);
    } catch (error) {
      if (!(error instanceof FeedbackAlreadySubmittedError)) {
        throw error;
      }
    }

    this.feedbackAlreadyGiven = true;
  };
}
